import intlTelInput from "intl-tel-input";
import { setSourceHeader } from "general/source";

function init({ container, data, callbacks, hooks, form_name }) {
  window.rm = {
    callbacks,
    hooks: hooks || {},
    form: {
      show: show_form,
      submit: submit_form,
      hide: callbacks.hide,
      spinner: toggleSpinner,
    },
    utils: {
      addEventListener,
      setSourceHeader,
      loadScripts,
    },
  };

  window.rm.form.show({
    container,
    data,
    form_name,
  });
}

function submit_form({ container, data, form_name, button, activityFun }) {
  let form = container.querySelector(`form[name=${form_name}]`);
  let formData = new FormData(form);
  let spinner = toggleSpinner(button);
  let errors = toggleErrors();
  let activeFields = toggleActiveFields(
    form.querySelectorAll(
      "input:not([disabled]), select:not([disabled]), button:not([disabled])",
    ),
  );
  errors.off(form.querySelectorAll("[data-error]"));

  if (container.querySelector("[name=intl_contact_number]")) {
    return import(`./fields/intl_contact_number.js`).then(function (mod) {
      const error = mod.validate({ container, formData });
      if (error) {
        return Promise.reject(error)
      }
      return Promise.resolve()
    }).then(() => {
      submit_form_to_server();
    }).catch(error => {
      errors.on(error, container);
    });
  }

  return submit_form_to_server();

  function submit_form_to_server() {
    spinner.on();
    errors.off(form.querySelectorAll("[data-error]"));
    activeFields.off();

    let headers = window.rm.utils.setSourceHeader({
      Accept: "text/html",
    });
    fetch(`/form/${form.getAttribute("name")}/`, {
      method: "POST",
      body: formData,
      headers,
    })
      .then(function (response) {
        if (!response.ok) {
          window.rm.callbacks.errors.showErrorModal(
            "Something went wrong!",
          );
          window.rm.callbacks.hide();
          if (window.rm.reservation) {
            window.rm.reservation.cancel();
          }
        }
        return response;
      })
      .then(function (response) {
        return response.json();
      })
      .then(function (json) {
        activeFields.on();
        spinner.off();

        if (json.errors) {
          errors.on(json.errors, container);
        } else if (json.redirect_url) {
          window.location = json.redirect_url;
        } else if (json.html) {
          const keepForm = container.querySelector("[data-keep-form]");
          const hasError = json.html.includes("data-error");

          if (keepForm) {
            container.insertAdjacentHTML("afterbegin", json.html);
          } else {
            container.innerHTML = json.html;
          }

          if (window.rm.reservation) {
            window.rm.reservation.clear();
          }

          let args = activityFun(formData);
          if (args) {
            window.rm.callbacks.registerUserActivity(...args);
          }

          let scripts = [];

          if (hasError) {
            scripts.push("error");

            if (container.querySelector('[data-script="appointment-error"]')) {
              scripts.push("forms/appointment/appointment-error");
            }
          } else {
            if (container.querySelector('[data-script="rmcp"]')) {
              scripts.push("forms/appointment/rmcp");
            }

            if (container.querySelector('[data-script="rmpp"]')) {
              scripts.push("forms/appointment/rmpp");
            }

            if (window.rm.hooks.hasOwnProperty("after_submit_form")) {
              window.rm.hooks.after_submit_form();
            }
          }

          Promise.all(scripts.map((s) => import(`./${s}.js`))).then(function (
            modules,
          ) {
            for (let mod of modules) {
              mod.init({ container, data });
            }
          });
        }
      });
  }
}

function show_form({ container, data, form_name }) {
  let headers = window.rm.utils.setSourceHeader({
    Accept: "text/html",
  });

  let query = Object.entries(data).reduce(function (acc, [k, v]) {
    return `${acc}&${k}=${v}`;
  }, "");
  fetch(`/form/${form_name}/?${query}`, {
    headers,
  })
    .then(function (response) {
      if (!response.ok) {
        console.error(response);
        window.rm.callbacks.errors.showErrorModal(
          "Something went wrong!",
        );
        window.rm.callbacks.hide();
        window.rm.hooks.error_show_form(response)
      }
      return response;
    })
    .then(function (response) {
      return response.text();
    })
    .then(function (html) {
      if (window.rm.hooks.after_show_form) {
        window.rm.hooks.after_show_form();
      }

      container.innerHTML = html;

      window.rm.utils.loadScripts({ container, data });
    });
}

function loadScripts({ container, data }, ) {
  let scripts = [];

  if (container.querySelector("[data-success]")) {
    if (container.querySelector("form-actions")) {
      scripts.push("form-actions");
      scripts.push("fields/duration");
    }

    if (container.querySelector('[data-script="reservation"]')) {
      scripts.push("forms/appointment/reservation");
    }

    if (container.querySelector('[data-script="patient_identity_details"]')) {
      scripts.push("fields/patient_identity_details");
    }

    if (container.querySelector('[data-script="medical_aid_picker"]')) {
      scripts.push("fields/medical_aid_picker");
    }

    if (container.querySelector('[data-script="reason_for_visit"]')) {
      scripts.push("fields/reason_for_visit");
    }

    if (container.querySelector('[data-script="patient_address"]')) {
      scripts.push("fields/patient_address");
    }

    if (container.querySelector("[data-script=related_data_qty]")) {
      scripts.push("fields/related_data_qty");
    }

    if (container.querySelector("[data-script=intl_contact_number]")) {
      scripts.push("fields/intl_contact_number");
    }

    if (container.querySelector("[data-script=appointment_lead_survey]")) {
      scripts.push("forms/appointment_lead_survey");
    }
  } else {
    if (container.querySelector('[data-script="reservation-error"]')) {
      scripts.push("forms/appointment/reservation-error");
    }
  }

  Promise.all(scripts.map((s) => import(`./${s}.js`))).then(function (modules) {
    for (let mod of modules) {
      mod.init({ container, data });
    }
  });
}

function findParentTag(tag) {
  function inner(el) {
    if (el.parentElement.tagName.toLowerCase() == tag) {
      return el.parentElement;
    } else {
      return inner(el.parentElement);
    }
  }

  return inner;
}

function toggleSpinner(button) {
  function on() {
    button.setAttribute("spinner", "");
    button.setAttribute("disabled", "");
  }

  function off() {
    button.removeAttribute("spinner");
    button.removeAttribute("disabled");
  }

  return { on, off };
}

function toggleErrors() {
  function on(objErrors, container) {
    for (let [fieldName, arrayErrors] of Object.entries(objErrors)) {
      if (typeof(arrayErrors[0]) == "string") {
        let field = container.querySelector(
          `[name='${fieldName}']:not([type=hidden]):not(form)`,
        );
        if (field) {
          let error =
            findParentTag("field-component")(field).querySelector(
              "[data-error]",
            );
          error.innerHTML = arrayErrors.join("<br />");
          error.classList.add("has-error");
        }
      } else if (typeof(arrayErrors[0]) == "object") {
        for (let [fieldName, errorStr] of Object.entries(arrayErrors[0])) {
          let field = container.querySelector(
            `[name='${fieldName}']:not([type=hidden]):not(form)`,
          );
          if (field) {
            let error =
              findParentTag("field-component")(field).querySelector(
                "[data-error]",
              );
            error.innerHTML = errorStr
            error.classList.add("has-error");
          }
        }
      }
    }

    scrollToFirstError(container, container.querySelector(".has-error"));
  }

  function off(errorEls) {
    errorEls.forEach(function (error) {
      error.innerHTML = "";
      error.classList.remove("has-error");
    });
  }

  return { on, off };
}

function toggleActiveFields(activeFieldEls) {
  function on() {
    for (let input of activeFieldEls) {
      input.disabled = false;
    }
  }

  function off() {
    for (let input of activeFieldEls) {
      input.disabled = true;
    }
  }

  return { on, off };
}

function scrollToFirstError(container, firstErrorEl) {
  let scrollToEl =
    findParentTag("field-component")(firstErrorEl).querySelector("[name]");
  let scrollToElContainer =
    scrollToEl.closest("section[class*='field-component']")
    || scrollToEl.closest("field-component");

  if (scrollToElContainer) {
    let topBound = 70;
    let offsetTop = scrollToElContainer.offsetTop +
      container.getBoundingClientRect().y +
      window.scrollY -
      topBound;
    let scrollableContainer = window;

    if (
      container.querySelector("form[name=appointment]") &&
      container.querySelector("[data-portal]").dataset.portal == "rmpp"
    ) {
      scrollableContainer = container.closest(".k-content.k-state-active");
      topBound = scrollableContainer.getBoundingClientRect().y;
      offsetTop = scrollToElContainer.offsetTop;
    }

    const coords = scrollToElContainer.getBoundingClientRect();

    if (coords.top < topBound) {
      scrollableContainer.scrollTo({
        top: offsetTop,
        left: coords.x,
        behavior: "smooth",
      });
    }

    scrollToEl.focus({ preventScroll: coords.top < topBound });
  }
}

function addEventListener(el, eventName, func) {
  if (el) {
    el.addEventListener(eventName, function (event) {
      event.preventDefault();

      func(event, el);
    });
  }
}

export { init };
